import React, { useEffect, useState, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, getDoc, query, where } from 'firebase/firestore';
import { getAuth, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc } from 'firebase/firestore';

// Updated Repair interface to match Firestore fields
interface Repair {
    id: string;
    active?: boolean;
    businessId?: string;
    createdAt?: { seconds: number; nanoseconds: number } | string;
    customerAddress?: string;
    customerCity?: string;
    customerMobile?: string;
    customerName?: string;
    customerPincode?: string;
    customerState?: string;
    deliveryCode?: string;
    estimatedCost?: string;
    estimatedDelivery?: string;
    imageUrls?: string[];
    paidAmount?: string;
    problems?: string;
    productName?: string;
    productType?: string;
    remarks?: string;
    serialNumber?: string;
    status?: string;
    totalCost?: string;
    updatedAt?: { seconds: number; nanoseconds: number } | string;
    amount?: number | string;
    repairId?: string; // Added repairId to the interface
}

interface User {
    id: string;
    name?: string;
    email?: string;
    role?: string;
    businessName?: string;
    // ...add any other fields you use
}

const Dashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [businessUsers, setBusinessUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalUserCount, setTotalUserCount] = useState(0);
    const [totalBusinessUserCount, setTotalBusinessUserCount] = useState(0);
    const [searchBusiness, setSearchBusiness] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const usersPerPage = 10;
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState<string | null>(null);
    const [roleLoading, setRoleLoading] = useState(true);
    const [serviceId, setServiceId] = React.useState('');
    const [repair, setRepair] = React.useState<Repair | null>(null);
    const [fetching, setFetching] = React.useState(false);
    const [userError, setUserError] = React.useState('');
    const [modalImage, setModalImage] = React.useState<string | null>(null);
    const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
    const [currentUserMobile, setCurrentUserMobile] = React.useState<string | null>(null);
    const [showSearch, setShowSearch] = React.useState(true);
    const [businessInfo, setBusinessInfo] = React.useState<{ name?: string; mobile?: string; businessName?: string } | null>(null);
    const [businessLoading, setBusinessLoading] = React.useState(false);

    const handleLogout = async () => {
        const auth = getAuth();
        await signOut(auth);
        navigate('/signin');
    };

    // Fetch user role, UID, and mobile from Firestore/Auth
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
            if (user) {
                setCurrentUserId(user.uid);
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserRole(data.role || null);
                    setCurrentUserMobile(data.mobile || data.customerMobile || null);
                } else {
                    setUserRole(null);
                    setCurrentUserMobile(null);
                }
            } else {
                setUserRole(null);
                setCurrentUserId(null);
                setCurrentUserMobile(null);
            }
            setRoleLoading(false);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (userRole !== 'sa') return;
        const fetchData = async () => {
            setLoading(true);
            const usersSnap = await getDocs(collection(db, 'users'));
            const repairsSnap = await getDocs(collection(db, 'repairs'));
            const storesSnap = await getDocs(collection(db, 'stores'));

            const allUsers: User[] = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
            const repairsData = repairsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Repair[];
            const storesData = storesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];

            // Debug logging to understand data structure
            console.log('All Stores Data:', storesData);
            console.log('All Repairs Data:', repairsData);
            console.log('Sample Repair:', repairsData[0]);
            console.log('Sample Store:', storesData[0]);

            // Set total users count
            setTotalUserCount(allUsers.length);

            // Use selected month/year for filtering
            const currentMonth = selectedMonth;
            const currentYear = selectedYear;

            // Get unique business owners from stores
            const businessOwnerIds = storesData
                .map(store => store.ownerId)
                .filter((ownerId, index, self) => self.indexOf(ownerId) === index);

            // Get business owner details and their performance
            const businessOwnersData = businessOwnerIds.map(ownerId => {
                // Find user data for this owner
                const userData = allUsers.find(user => user.id === ownerId);

                // Count stores owned by this business owner
                const storesOwned = storesData.filter(store => store.ownerId === ownerId).length;

                // Count repairs for all stores owned by this business owner
                const ownerStores = storesData.filter(store => store.ownerId === ownerId);
                const storeIds = ownerStores.map(store => store.id);

                // Count repairs for selected month for this business owner's stores
                const totalRepairs = repairsData.filter(r =>
                    storeIds.includes((r as any).storeId || r.businessId || (r as any).store) &&
                    r.createdAt &&
                    typeof r.createdAt !== 'string' &&
                    new Date(r.createdAt.seconds * 1000).getMonth() === currentMonth &&
                    new Date(r.createdAt.seconds * 1000).getFullYear() === currentYear
                ).length;

                // Debug logging to understand the data structure
                console.log('Business Owner ID:', ownerId);
                console.log('Owner Stores:', ownerStores);
                console.log('Store IDs:', storeIds);
                console.log('All Repairs:', repairsData);
                console.log('Sample repair fields:', repairsData[0] ? Object.keys(repairsData[0]) : 'No repairs');
                console.log('Repairs with matching storeId/businessId:', repairsData.filter(r => storeIds.includes((r as any).storeId || r.businessId || (r as any).store)));
                console.log('Total Repairs for this owner:', totalRepairs);

                return {
                    id: ownerId,
                    businessName: userData?.businessName || userData?.name || 'Unknown Business',
                    email: userData?.email || 'No email',
                    totalRepairs,
                    storesOwned
                };
            });

            // Set total business users count (business owners with stores)
            setTotalBusinessUserCount(businessOwnersData.length);
            setBusinessUsers(businessOwnersData);
            setLoading(false);
        };
        fetchData();
    }, [userRole, selectedMonth, selectedYear]);

    // Example mock data for non-admin dashboard
    const activeRepairs: Repair[] = [
        {
            id: '1',
            customerName: 'Haab',
            customerMobile: '9876543210',
            status: 'sent_to_service_center',
            problems: 'Hddbh',
            updatedAt: '25/6/2025, 1:24:46 pm',
            imageUrls: ['https://via.placeholder.com/40'],
            amount: 1200,
        },
        {
            id: '2',
            customerName: 'Haab',
            customerMobile: '9876543210',
            status: 'inprogress',
            problems: 'Bb',
            updatedAt: '25/6/2025, 1:23:00 pm',
            imageUrls: ['https://via.placeholder.com/40'],
            amount: 800,
        },
    ];

    const completedRepairs: Repair[] = [
        {
            id: '3',
            customerName: 'Haab',
            customerMobile: '9876543210',
            status: 'delivered',
            problems: 'Dell',
            updatedAt: '24/6/2025',
            amount: 2800,
            imageUrls: ['https://via.placeholder.com/40'],
        },
        {
            id: '4',
            customerName: 'Haab',
            customerMobile: '9876543210',
            status: 'delivered',
            problems: 'SII',
            updatedAt: '24/6/2025',
            amount: 1500,
            imageUrls: ['https://via.placeholder.com/40'],
        },
    ];

    const filteredRepairs = (activeTab === 'active' ? activeRepairs : completedRepairs)
        .filter(repair =>
            (repair.customerMobile ?? '').includes(searchQuery) ||
            (repair.customerName ?? '').toLowerCase().includes(searchQuery.toLowerCase())
        )
        .filter(repair => statusFilter === 'All' || repair.status === statusFilter);

    if (roleLoading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-cyan-400 text-xl font-bold animate-pulse">Loading...</div>
            </div>
        );
    }

    if (userRole === 'sa') {
        // Filter business users by search
        const filteredBusinessUsers = businessUsers.filter(
            (user: any) =>
            (user.businessName?.toLowerCase().includes(searchBusiness.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchBusiness.toLowerCase()))
        );
        // Pagination logic
        const indexOfLastUser = currentPage * usersPerPage;
        const indexOfFirstUser = indexOfLastUser - usersPerPage;
        const currentUsers = filteredBusinessUsers.slice(indexOfFirstUser, indexOfLastUser);
        const totalPages = Math.ceil(filteredBusinessUsers.length / usersPerPage);
        return (
            <div className="min-h-screen bg-gray-900">
                <header className="bg-cyan-600 py-4 flex items-center justify-between">
                    <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-white">Fixisy Admin Dashboard</h1>
                        <div className="flex gap-2">
                            <button
                                onClick={() => navigate('/support-admin')}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 w-full sm:w-auto font-semibold"
                            >
                                View Support Issues
                            </button>
                            <button
                                onClick={() => navigate('/distributor-recharge')}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 w-full sm:w-auto font-semibold"
                            >
                                Recharge Distributors
                            </button>
                            <button
                                onClick={handleLogout}
                                className="ml-0 sm:ml-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 w-full sm:w-auto"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </header>
                <main className="container mx-auto px-4 py-6 sm:py-8">
                    {/* Month Filter */}
                    <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6">
                        <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">Filter by Month</h3>
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                            <div>
                                <label className="block text-gray-300 text-sm mb-2">Month</label>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                    className="px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                >
                                    <option value={0}>January</option>
                                    <option value={1}>February</option>
                                    <option value={2}>March</option>
                                    <option value={3}>April</option>
                                    <option value={4}>May</option>
                                    <option value={5}>June</option>
                                    <option value={6}>July</option>
                                    <option value={7}>August</option>
                                    <option value={8}>September</option>
                                    <option value={9}>October</option>
                                    <option value={10}>November</option>
                                    <option value={11}>December</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-300 text-sm mb-2">Year</label>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                    className="px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-6">
                                <button
                                    onClick={() => {
                                        setSelectedMonth(new Date().getMonth());
                                        setSelectedYear(new Date().getFullYear());
                                    }}
                                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                                >
                                    Reset to Current Month
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Summary Boxes */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6 sm:mb-8">
                        <div className="flex-1 bg-gray-800 rounded-lg p-4 sm:p-6 flex flex-col items-center shadow">
                            <span className="text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">Total Users</span>
                            <span className="text-2xl sm:text-3xl font-bold text-cyan-400">{totalUserCount}</span>
                        </div>
                        <div className="flex-1 bg-gray-800 rounded-lg p-4 sm:p-6 flex flex-col items-center shadow">
                            <span className="text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">Total Business Users</span>
                            <span className="text-2xl sm:text-3xl font-bold text-cyan-400">{totalBusinessUserCount}</span>
                        </div>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-cyan-400 mb-4 sm:mb-6">All Business Users - Performance Overview</h2>
                    {/* Search input for business users */}
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search by business name or email"
                            value={searchBusiness}
                            onChange={e => { setSearchBusiness(e.target.value); setCurrentPage(1); }}
                            className="w-full max-w-xs px-4 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        />
                    </div>
                    {loading ? (
                        <div className="text-white">Loading...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-gray-800 rounded-lg overflow-hidden text-xs sm:text-base">
                                <thead>
                                    <tr>
                                        <th className="px-2 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Business Name</th>
                                        <th className="px-2 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Email</th>
                                        <th className="px-2 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Total Repairs ({new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})</th>
                                        <th className="px-2 sm:px-6 py-2 sm:py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Stores Owned</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentUsers.map((user: any) => (
                                        <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/30">
                                            <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-white font-semibold">{user.businessName}</td>
                                            <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-gray-300">{user.email}</td>
                                            <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-green-400 font-bold text-base sm:text-lg">{user.totalRepairs || 0}</td>
                                            <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-blue-400 font-bold text-base sm:text-lg">{user.storesOwned || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination Controls */}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 mt-4">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded w-full sm:w-auto ${currentPage === 1 ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-cyan-600 text-white hover:bg-cyan-700'}`}
                                >
                                    Prev
                                </button>
                                <span className="text-gray-300">Page {currentPage} of {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    className={`px-3 py-1 rounded w-full sm:w-auto ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-cyan-600 text-white hover:bg-cyan-700'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </main>
                <footer className="bg-gray-800 py-4 text-center text-gray-400 text-xs sm:text-sm">
                    &copy; {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
                </footer>
            </div>
        );
    }

    // For all other users, show Service ID input and details if role === 'user'
    if (userRole === 'user') {
        const handleFetchRepair = async (e: React.FormEvent) => {
            e.preventDefault();
            setFetching(true);
            setUserError('');
            setRepair(null);
            setBusinessInfo(null);
            try {
                // Always treat the input as the repair id for Firestore lookup
                const repairId = serviceId.trim();
                const q = query(collection(db, 'repairs'), where('repairId', '==', repairId));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const docSnap = querySnapshot.docs[0];
                    const data = docSnap.data();
                    if (currentUserMobile && data.customerMobile === currentUserMobile) {
                        setRepair({ id: docSnap.id, ...data } as Repair);
                        setShowSearch(false);
                        // Fetch business info
                        if (data.businessId) {
                            setBusinessLoading(true);
                            const businessDoc = await getDoc(doc(db, 'users', data.businessId));
                            if (businessDoc.exists()) {
                                const b = businessDoc.data();
                                setBusinessInfo({
                                    name: b.businessName || b.name || '',
                                    mobile: b.mobile || b.phone || b.ownerNumber || '',
                                    businessName: b.businessName || '',
                                });
                            } else {
                                setBusinessInfo(null);
                            }
                            setBusinessLoading(false);
                        } else {
                            setBusinessInfo(null);
                        }
                    } else {
                        setUserError('You are not authorized to view this repair.');
                    }
                } else {
                    setUserError('No repair found with this Repair ID.');
                }
            } catch (err) {
                setUserError('Error fetching repair.');
            }
            setFetching(false);
        };
        const handleTrackMore = () => {
            setRepair(null);
            setServiceId('');
            setUserError('');
            setShowSearch(true);
            setBusinessInfo(null);
        };
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col">
                {/* Header */}
                <header className="bg-cyan-600 py-4 flex items-center justify-between">
                    <div className="container mx-auto px-4 flex items-center justify-between">
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 object-contain" />
                            Fixisy
                        </h1>
                        <button
                            onClick={handleLogout}
                            className="ml-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            Logout
                        </button>
                    </div>
                </header>
                {/* Main Content */}
                <main className="flex-1 flex flex-col items-center justify-center px-4">
                    {showSearch && (
                        <form onSubmit={handleFetchRepair} className="w-full max-w-md bg-gray-800 p-8 rounded-xl shadow-lg mb-8 border border-cyan-900">
                            <label className="block text-gray-300 mb-2 text-lg font-semibold">Enter Repair ID</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={serviceId}
                                    onChange={e => setServiceId(e.target.value)}
                                    className="flex-1 px-4 py-2 rounded-lg bg-gray-900 text-white border border-cyan-700 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-500"
                                    placeholder="Repair ID"
                                    required
                                />
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 font-semibold shadow"
                                    disabled={fetching}
                                >
                                    {fetching ? 'Searching...' : 'Search'}
                                </button>
                            </div>
                            {userError && <div className="mt-4 text-red-400 text-center">{userError}</div>}
                        </form>
                    )}
                    {repair && !showSearch && (
                        <div className="w-full max-w-md bg-gray-800 p-6 rounded-xl shadow-lg border border-cyan-900">
                            <h2 className="text-2xl font-bold text-cyan-400 mb-4">Repair Details</h2>
                            <div className="space-y-2 text-base">
                                <div><span className="font-semibold text-gray-300">Repair ID:</span> <span className="text-white">{repair.id}</span></div>
                                {businessLoading ? (
                                    <div className="text-cyan-400">Loading business info...</div>
                                ) : businessInfo ? (
                                    <>
                                        <div><span className="font-semibold text-gray-300">Business Name:</span> <span className="text-white">{businessInfo.businessName}</span></div>
                                        <div><span className="font-semibold text-gray-300">Business Number:</span> <span className="text-white">{businessInfo.mobile}</span></div>
                                    </>
                                ) : (
                                    <div className="text-gray-400">Business info not available.</div>
                                )}
                                <div><span className="font-semibold text-gray-300">Product:</span> <span className="text-white">{repair.productName} ({repair.productType})</span></div>
                                <div><span className="font-semibold text-gray-300">Problems:</span> <span className="text-white">{repair.problems}</span></div>
                                <div><span className="font-semibold text-gray-300">Status:</span> <span className="text-white capitalize">{repair.status}</span></div>
                                <div><span className="font-semibold text-gray-300">Amount:</span> <span className="text-white">{repair.amount ? `₹${repair.amount}` : (repair.estimatedCost ? `₹${repair.estimatedCost}` : '-')}</span></div>
                                <div><span className="font-semibold text-gray-300">Delivery Code:</span> <span className="text-white">{repair.deliveryCode || '-'}</span></div>
                                <div><span className="font-semibold text-gray-300">Updated At:</span> <span className="text-white">{repair.updatedAt ? (typeof repair.updatedAt === 'string' ? repair.updatedAt : new Date(repair.updatedAt.seconds * 1000).toLocaleString()) : '-'}</span></div>
                            </div>
                            {repair.imageUrls && repair.imageUrls.length > 0 && (
                                <div className="mt-4">
                                    <span className="font-semibold text-gray-300">Images:</span>
                                    <div className="grid grid-cols-3 gap-3 mt-2">
                                        {repair.imageUrls.map((url, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className="focus:outline-none group"
                                                onClick={() => url ? setModalImage(url) : null}
                                            >
                                                {url ? (
                                                    <img
                                                        src={url}
                                                        alt="Repair"
                                                        className="h-20 w-20 object-cover rounded border-2 border-gray-700 group-hover:border-cyan-400 transition duration-200 shadow"
                                                    />
                                                ) : (
                                                    <div className="h-20 w-20 flex items-center justify-center bg-gray-700 rounded border-2 border-gray-700">
                                                        {/* Simple SVG avatar icon */}
                                                        <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                                            <circle cx="12" cy="8" r="4" />
                                                            <path d="M4 20c0-4 8-4 8-4s8 0 8 4v2H4v-2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={handleTrackMore}
                                className="mt-6 px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 font-semibold shadow w-full"
                            >
                                Track More
                            </button>
                        </div>
                    )}
                    {/* Modal for image preview */}
                    {modalImage && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setModalImage(null)}>
                            <div className="relative">
                                <img src={modalImage} alt="Full Repair" className="max-h-[80vh] max-w-[90vw] rounded-xl border-4 border-cyan-400 shadow-2xl" />
                                <button
                                    onClick={() => setModalImage(null)}
                                    className="absolute top-2 right-2 bg-cyan-600 text-white rounded-full p-2 shadow-lg hover:bg-cyan-700 focus:outline-none"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                </main>
                {/* Footer */}
                <footer className="bg-gray-800 py-4 text-center text-gray-400 text-xs sm:text-sm">
                    &copy; {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
                </footer>
            </div>
        );
    }

    // For distributors, redirect to DistributorDashboard
    if (userRole === 'distributor') {
        // Import and render DistributorDashboard
        const DistributorDashboard = React.lazy(() => import('./DistributorDashboard'));
        return (
            <Suspense fallback={
                <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                    <div className="text-cyan-400 text-xl font-bold animate-pulse">Loading...</div>
                </div>
            }>
                <DistributorDashboard />
            </Suspense>
        );
    }

    // For all other users, show Coming Soon
    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Header */}
            <header className="bg-cyan-600 py-4 flex items-center justify-between">
                <div className="container mx-auto px-4 flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 object-contain" />
                        Fixisy
                    </h1>
                    <button
                        onClick={handleLogout}
                        className="ml-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Logout
                    </button>
                </div>
            </header>
            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center">
                <h1 className="text-3xl font-bold text-cyan-400">Coming Soon</h1>
            </main>
            {/* Footer */}
            <footer className="bg-gray-800 py-4 text-center text-gray-400 text-xs sm:text-sm">
                &copy; {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
            </footer>
        </div>
    );
};

// Icon Components
const PhoneIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const WhatsAppIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const WrenchIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

const CheckIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
);

const UserIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

export default Dashboard; 